import { useState, useEffect } from "react";

interface CurrencyInfo {
  code: string;
  symbol: string;
  rate: number; // rate from EUR
}

// Approximate EUR exchange rates (updated periodically — no API key needed)
const CURRENCY_MAP: Record<string, CurrencyInfo> = {
  US: { code: "USD", symbol: "$", rate: 1.08 },
  GB: { code: "GBP", symbol: "£", rate: 0.86 },
  JP: { code: "JPY", symbol: "¥", rate: 162.5 },
  AU: { code: "AUD", symbol: "A$", rate: 1.66 },
  CA: { code: "CAD", symbol: "C$", rate: 1.47 },
  CH: { code: "CHF", symbol: "CHF", rate: 0.96 },
  CN: { code: "CNY", symbol: "¥", rate: 7.85 },
  IN: { code: "INR", symbol: "₹", rate: 90.2 },
  BR: { code: "BRL", symbol: "R$", rate: 5.45 },
  MX: { code: "MXN", symbol: "MX$", rate: 18.5 },
  KR: { code: "KRW", symbol: "₩", rate: 1420 },
  SE: { code: "SEK", symbol: "kr", rate: 11.2 },
  NO: { code: "NOK", symbol: "kr", rate: 11.5 },
  DK: { code: "DKK", symbol: "kr", rate: 7.46 },
  PL: { code: "PLN", symbol: "zł", rate: 4.32 },
  CZ: { code: "CZK", symbol: "Kč", rate: 25.1 },
  HU: { code: "HUF", symbol: "Ft", rate: 395 },
  RO: { code: "RON", symbol: "lei", rate: 4.97 },
  BG: { code: "BGN", symbol: "лв", rate: 1.96 },
  TR: { code: "TRY", symbol: "₺", rate: 35.2 },
  IL: { code: "ILS", symbol: "₪", rate: 3.95 },
  AE: { code: "AED", symbol: "د.إ", rate: 3.97 },
  SA: { code: "SAR", symbol: "﷼", rate: 4.05 },
  EG: { code: "EGP", symbol: "E£", rate: 52.5 },
  ZA: { code: "ZAR", symbol: "R", rate: 19.8 },
  NG: { code: "NGN", symbol: "₦", rate: 1650 },
  UA: { code: "UAH", symbol: "₴", rate: 44.5 },
  RU: { code: "RUB", symbol: "₽", rate: 98 },
  PH: { code: "PHP", symbol: "₱", rate: 60.5 },
  TH: { code: "THB", symbol: "฿", rate: 37.8 },
  MY: { code: "MYR", symbol: "RM", rate: 4.85 },
  SG: { code: "SGD", symbol: "S$", rate: 1.46 },
  NZ: { code: "NZD", symbol: "NZ$", rate: 1.79 },
  TW: { code: "TWD", symbol: "NT$", rate: 34.5 },
  PK: { code: "PKR", symbol: "₨", rate: 300 },
  ID: { code: "IDR", symbol: "Rp", rate: 17200 },
  VN: { code: "VND", symbol: "₫", rate: 27000 },
  AR: { code: "ARS", symbol: "AR$", rate: 950 },
  CL: { code: "CLP", symbol: "CL$", rate: 1020 },
  CO: { code: "COP", symbol: "COL$", rate: 4350 },
  QA: { code: "QAR", symbol: "﷼", rate: 3.93 },
  KW: { code: "KWD", symbol: "د.ك", rate: 0.33 },
  BH: { code: "BHD", symbol: "BD", rate: 0.41 },
};

// Eurozone countries — show EUR natively
const EUROZONE = new Set([
  "DE", "FR", "IT", "ES", "NL", "BE", "AT", "IE", "PT", "FI",
  "GR", "LU", "MT", "CY", "SK", "SI", "EE", "LV", "LT", "HR",
]);

export const useLocalCurrency = () => {
  const [currency, setCurrency] = useState<CurrencyInfo>({ code: "EUR", symbol: "€", rate: 1 });
  const [countryCode, setCountryCode] = useState<string | null>(null);

  useEffect(() => {
    // Use timezone to guess country (no API needed, instant)
    const detectCountry = () => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const cc = timezoneToCountry(tz);
        if (cc) {
          setCountryCode(cc);
          if (!EUROZONE.has(cc) && CURRENCY_MAP[cc]) {
            setCurrency(CURRENCY_MAP[cc]);
          }
        }
      } catch {
        // default EUR
      }
    };
    detectCountry();
  }, []);

  const formatPrice = (eurAmount: number): string => {
    if (currency.code === "EUR") return `€${eurAmount.toFixed(2)}`;
    const converted = eurAmount * currency.rate;
    // Format nicely
    if (currency.rate > 100) {
      return `${currency.symbol}${Math.round(converted).toLocaleString()}`;
    }
    return `${currency.symbol}${converted.toFixed(2)}`;
  };

  const formatPriceWithEur = (eurAmount: number): string => {
    if (currency.code === "EUR") return `€${eurAmount.toFixed(2)}`;
    const local = formatPrice(eurAmount);
    return `${local} (€${eurAmount.toFixed(2)})`;
  };

  const formatUsdPriceWithUsd = (usdAmount: number): string => {
    if (currency.code === "USD") return `$${usdAmount.toFixed(2)}`;
    // Convert USD → EUR → local
    const usdToEurRate = 1 / (CURRENCY_MAP["US"]?.rate || 1.08);
    const eurAmount = usdAmount * usdToEurRate;
    if (currency.code === "EUR") {
      return `€${eurAmount.toFixed(2)} ($${usdAmount.toFixed(2)})`;
    }
    const localAmount = eurAmount * currency.rate;
    const localFormatted = currency.rate > 100
      ? `${currency.symbol}${Math.round(localAmount).toLocaleString()}`
      : `${currency.symbol}${localAmount.toFixed(2)}`;
    return `${localFormatted} ($${usdAmount.toFixed(2)})`;
  };

  return { currency, countryCode, formatPrice, formatPriceWithEur, formatUsdPriceWithUsd };
};

// Timezone → country code mapping (major timezones)
function timezoneToCountry(tz: string): string | null {
  const map: Record<string, string> = {
    "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US",
    "America/Los_Angeles": "US", "America/Anchorage": "US", "Pacific/Honolulu": "US",
    "America/Phoenix": "US", "America/Indiana/Indianapolis": "US",
    "Europe/London": "GB", "Europe/Paris": "FR", "Europe/Berlin": "DE",
    "Europe/Rome": "IT", "Europe/Madrid": "ES", "Europe/Amsterdam": "NL",
    "Europe/Brussels": "BE", "Europe/Vienna": "AT", "Europe/Dublin": "IE",
    "Europe/Lisbon": "PT", "Europe/Helsinki": "FI", "Europe/Athens": "GR",
    "Europe/Luxembourg": "LU", "Europe/Zurich": "CH", "Europe/Stockholm": "SE",
    "Europe/Oslo": "NO", "Europe/Copenhagen": "DK", "Europe/Warsaw": "PL",
    "Europe/Prague": "CZ", "Europe/Budapest": "HU", "Europe/Bucharest": "RO",
    "Europe/Sofia": "BG", "Europe/Zagreb": "HR", "Europe/Ljubljana": "SI",
    "Europe/Bratislava": "SK", "Europe/Tallinn": "EE", "Europe/Riga": "LV",
    "Europe/Vilnius": "LT", "Europe/Valletta": "MT", "Asia/Nicosia": "CY",
    "Europe/Istanbul": "TR", "Europe/Moscow": "RU", "Europe/Kyiv": "UA",
    "Asia/Tokyo": "JP", "Asia/Shanghai": "CN", "Asia/Kolkata": "IN",
    "Asia/Seoul": "KR", "Asia/Taipei": "TW", "Asia/Singapore": "SG",
    "Asia/Kuala_Lumpur": "MY", "Asia/Bangkok": "TH", "Asia/Jakarta": "ID",
    "Asia/Manila": "PH", "Asia/Ho_Chi_Minh": "VN", "Asia/Karachi": "PK",
    "Asia/Dubai": "AE", "Asia/Riyadh": "SA", "Asia/Qatar": "QA",
    "Asia/Bahrain": "BH", "Asia/Kuwait": "KW", "Asia/Jerusalem": "IL",
    "Africa/Cairo": "EG", "Africa/Johannesburg": "ZA", "Africa/Lagos": "NG",
    "America/Sao_Paulo": "BR", "America/Mexico_City": "MX", "America/Toronto": "CA",
    "America/Vancouver": "CA", "America/Argentina/Buenos_Aires": "AR",
    "America/Santiago": "CL", "America/Bogota": "CO",
    "Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Perth": "AU",
    "Pacific/Auckland": "NZ",
  };
  return map[tz] || null;
}
